/**
 * Location/venue display + hierarchy helpers.
 *
 * Locations come from the API as Mongoose docs / lean objects: `parentLocation`
 * may be a populated object, a bare ObjectId/string, or absent. The types below
 * are deliberately loose (`LocationLike`) to accept all of those shapes.
 */

export interface LocationLike {
  _id?: any;
  name?: string;
  /** Populated parent object, bare id (string/ObjectId), or absent. */
  parentLocation?: any;
  [key: string]: any;
}

export type LocationWithSubVenues = LocationLike & { subVenues: LocationLike[] };

export type FlattenedLocation = LocationLike & {
  displayName: string | undefined;
  isSubVenue: boolean;
  depth: number;
  parentVenueName?: string;
};

/**
 * Gets the display name for a location/venue.
 * Handles hierarchical venues by combining parent and child names.
 *
 * @param location - The location object
 * @param parentLocation - Optional parent location (if not populated on location object)
 * @returns The display name for the location (e.g., "North High School - Court 1")
 */
export default function getLocationDisplayName(
  location: LocationLike | null | undefined,
  parentLocation: LocationLike | null = null,
): string {
  if (!location) {
    return 'Unknown Location';
  }

  const locationName = location.name || '';

  // Use provided parentLocation or the one populated on the location object
  const parent = parentLocation || location.parentLocation;

  if (parent && typeof parent === 'object' && parent.name) {
    return `${parent.name} - ${locationName}`;
  }

  return locationName || 'Unknown Location';
}

/**
 * Organizes a flat list of locations into a hierarchical structure.
 * Returns top-level venues with their sub-venues nested.
 *
 * @param locations - Flat array of location objects
 * @returns Array of top-level locations, each with subVenues array
 */
export function organizeLocationsHierarchy(
  locations: LocationLike[] | null | undefined,
): LocationWithSubVenues[] {
  if (!locations || !Array.isArray(locations)) {
    return [];
  }

  const locationMap = new Map<any, LocationLike>();
  const topLevel: LocationWithSubVenues[] = [];
  const subVenues: LocationLike[] = [];

  // First pass: separate top-level and sub-venues
  locations.forEach(loc => {
    locationMap.set(loc._id?.toString() || loc._id, loc);

    if (!loc.parentLocation) {
      topLevel.push({ ...loc, subVenues: [] });
    } else {
      subVenues.push(loc);
    }
  });

  // Second pass: attach sub-venues to their parents
  subVenues.forEach(subVenue => {
    const parentId = typeof subVenue.parentLocation === 'object'
      ? subVenue.parentLocation._id?.toString() || subVenue.parentLocation._id
      : subVenue.parentLocation?.toString() || subVenue.parentLocation;

    const parent = topLevel.find(p =>
      (p._id?.toString() || p._id) === parentId
    );

    if (parent) {
      parent.subVenues.push(subVenue);
    }
  });

  return topLevel;
}

/**
 * Flattens a hierarchical location structure for use in dropdowns.
 * Returns an array with parent venues followed by their sub-venues.
 *
 * @param locations - Flat or hierarchical array of locations
 * @returns Flattened array with display names and hierarchy info
 */
export function flattenLocationsForSelect(
  locations: LocationLike[] | null | undefined,
): FlattenedLocation[] {
  const hierarchy = organizeLocationsHierarchy(locations);
  const result: FlattenedLocation[] = [];

  hierarchy.forEach(venue => {
    // Add the parent venue
    result.push({
      ...venue,
      displayName: venue.name,
      isSubVenue: false,
      depth: 0,
    });

    // Add sub-venues with indentation info
    (venue.subVenues || []).forEach(subVenue => {
      result.push({
        ...subVenue,
        displayName: `${venue.name} - ${subVenue.name}`,
        isSubVenue: true,
        depth: 1,
        parentVenueName: venue.name,
      });
    });
  });

  return result;
}
